import {groupByProperties, visitGroups} from './invoicing_generate_lines_utils';
import {TIME_REG_GROUPINGS} from './invoicing_groupings';
import {WorkflowCategories} from '../../../../constants';

/**
 * Groups time registrations into the aggregate rows shown as work detail on a fixed-price
 * invoice.
 *
 * This module is deliberately pure and Relay-free so it can be shared by the creation-flow
 * panel, the PDF section and the retainer period flow, and so it is trivially testable.
 *
 * The load-bearing invariant lives here as an omission: a WorkDetailRow carries no
 * time-registration id, and nothing in this file reads `invoiced`, `invoiceId` or
 * `invoiceEntryId`. The only write path that marks time as invoiced is reached through
 * InvoiceEntryInput.timeRegIds, so a row that cannot express a time-registration id cannot
 * reach it. Listing work on an invoice must never mark that work invoiced. Do not add one.
 */

/**
 * Internal grouping key for time that cannot carry the requested grouping dimension, so that all of
 * it collapses into one bucket.
 *
 * This is a key, never a label. It is deliberately NOT what lands on the row: the emitted row
 * carries `groupLabel: null`, and the wording is resolved when rendered — see
 * `workDetailUngroupedLabelKey` in invoicing_groupings.ts. Persisting it as a label is how a
 * developer placeholder reached a client-facing invoice.
 */
export const WORK_DETAIL_UNGROUPED_KEY = '__WORK_DETAIL_UNGROUPED__';

export type WorkDetailRow = {
	key: string;
	/**
	 * Null when the chosen dimension is absent for this row's time, which the presentation layer
	 * turns into a dimension-appropriate label.
	 */
	groupLabel: string | null;
	sortOrder: number;
	minutes: number;
	firstDate: string | null;
	lastDate: string | null;
	rate: number | null;
	value: number | null;
	removed: boolean;
};

/**
 * A project as work detail reads it: the label the PROJECT dimension groups by, and the rate card
 * whose currency the registration's rate is stated in.
 */
export type WorkDetailProject = {
	id?: string;
	name?: string | null;
	rateCard?: {currency?: string | null} | null;
};

/**
 * The subset of TimeRegType this engine reads, i.e. what the callers' queries select. Phase and
 * sprint are absent at the top level on purpose — they are not fields on TimeRegType and have to be
 * traversed through the task.
 */
export type WorkDetailTimeReg = {
	id: string;
	year: number;
	month: number;
	day: number;
	minutesRegistered?: number | null;
	/**
	 * The hourly rate this time was logged at, in the project's rate-card currency.
	 *
	 * This, not `price`, is what values a work-detail row. `price` is the BILLABLE portion only —
	 * the backend derives it from `billableMinutesRegistered` — whereas a work-detail row states all
	 * the work that was done. Multiplying this rate by those same total hours is what makes the Hours,
	 * Rate and Value columns describe one consistent quantity; summing `price` against total hours
	 * silently understated the rate on any company using the billable split.
	 */
	rate?: number | null;
	notes?: string | null;
	person?: {id?: string; firstName?: string | null; lastName?: string | null} | null;
	role?: {id?: string; name?: string | null} | null;
	/**
	 * The project the time was logged against, set only when it was logged against the project
	 * directly. `rateCard.currency` is the currency the rate is stated in, which is why it travels
	 * with the registration rather than being read once off the invoice: a single invoice can span
	 * projects on different rate cards.
	 *
	 * Resolve it through `workDetailProject` rather than reading it here — task-logged time leaves this
	 * null and carries the project on the task instead.
	 */
	project?: WorkDetailProject | null;
	task?: {
		id?: string;
		name?: string | null;
		/** Where task-logged time carries its project. See `workDetailProject`. */
		project?: WorkDetailProject | null;
		phase?: {id?: string; name?: string | null} | null;
		sprint?: {id?: string; name?: string | null} | null;
		/** Read only to keep the done-tasks grouping to done tasks. */
		statusColumnV2?: {category?: string | null} | null;
	} | null;
};

/**
 * The project a registration belongs to.
 *
 * Time logged against a task carries no project of its own: TimeRegType.project resolves from the
 * registration's own projectId, which only project-level time sets, so for task-logged time the
 * task's project is what identifies it. This is the same fallback the priced-line generator applies
 * (getAllTimeRegs in invoicing_generate_lines_timeregs.js), and the two have to agree — the priced
 * lines and the work detail describing them appear on one document.
 *
 * Without the fallback every task-logged registration reads as having no project at all, which merges
 * two projects' identically-named phases into a single row and collapses PROJECT grouping to one
 * bucket, and leaves the rate-card currency unresolved so amounts print under the wrong symbol.
 */
export const workDetailProject = (tr: WorkDetailTimeReg): WorkDetailProject | null => tr.project ?? tr.task?.project ?? null;

/** A registration tagged with the label it groups under, used only while grouping. */
type GroupedTimeReg = WorkDetailTimeReg & {__wdGroup: string; __wdProject: string};

export type WorkDetailConfig = {
	grouping: string;
	showRates: boolean;
	quantityDecimals: number;
	startDate?: string;
	endDate?: string;
	/**
	 * Converts one registration's own amount into the invoice's currency, mirroring what the
	 * priced-line generator does (see createQuantityAndUnitPrice in
	 * invoicing_generate_lines_timeregs.js).
	 *
	 * A registration's rate is denominated in its project's rate-card currency, while every amount on
	 * the rendered document is labelled with the invoice's currency symbol. Without this, a
	 * cross-currency invoice prints rate-card numbers under the wrong symbol — the amounts look
	 * plausible and are wrong by the exchange rate.
	 *
	 * Applied per registration rather than to the group total, because one group can hold work from
	 * several projects and so several source currencies.
	 *
	 * Omit it only when every registration is already in the invoice's currency; the amounts are then
	 * carried unchanged.
	 */
	priceToInvoiceCurrency?: (price: number, timeReg: WorkDetailTimeReg) => number;
};

const iso = (tr: WorkDetailTimeReg): string =>
	`${tr.year}-${String(tr.month).padStart(2, '0')}-${String(tr.day).padStart(2, '0')}`;

const personName = (tr: WorkDetailTimeReg): string =>
	tr.person ? `${tr.person.firstName ?? ''} ${tr.person.lastName ?? ''}`.trim() : '';

/**
 * Mirrors createDescription in invoicing_generate_lines_timeregs.js, minus the date range —
 * work-detail rows carry firstDate/lastDate as their own columns, so embedding dates in the
 * label would print them twice.
 *
 * Phase and sprint are NOT fields on TimeRegType; they hang off task. Time with no task
 * therefore cannot be phase- or sprint-grouped, and is bucketed under
 * WORK_DETAIL_UNGROUPED_KEY rather than being silently dropped.
 *
 * A name that is present but EMPTY buckets the same way as an absent one, so the row names the
 * dimension it could not resolve rather than rendering as a blank label.
 */
const labelFor = (tr: WorkDetailTimeReg, grouping: string): string => {
	switch (grouping) {
		case TIME_REG_GROUPINGS.PERSON:
			return personName(tr) || WORK_DETAIL_UNGROUPED_KEY;
		case TIME_REG_GROUPINGS.ROLE:
			return tr.role?.name || WORK_DETAIL_UNGROUPED_KEY;
		case TIME_REG_GROUPINGS.PHASE:
			return tr.task?.phase?.name || WORK_DETAIL_UNGROUPED_KEY;
		case TIME_REG_GROUPINGS.SPRINTS:
			return tr.task?.sprint?.name || WORK_DETAIL_UNGROUPED_KEY;
		case TIME_REG_GROUPINGS.TASK:
		case TIME_REG_GROUPINGS.DONE_TASK:
			return tr.task?.name || WORK_DETAIL_UNGROUPED_KEY;
		case TIME_REG_GROUPINGS.PROJECT:
			return workDetailProject(tr)?.name || WORK_DETAIL_UNGROUPED_KEY;
		case TIME_REG_GROUPINGS.NO_GROUPING_NOTES: {
			// Assembled from whichever parts exist, so a registration carrying only notes reads as the
			// notes rather than as a bare separator with nothing in front of it.
			const who = [tr.role?.name, personName(tr)].filter(Boolean).join(', ');
			return [who, tr.notes].filter(Boolean).join(' — ') || WORK_DETAIL_UNGROUPED_KEY;
		}
		case TIME_REG_GROUPINGS.NO_GROUPING:
		default:
			return [tr.role?.name, personName(tr)].filter(Boolean).join(', ') || WORK_DETAIL_UNGROUPED_KEY;
	}
};

/**
 * Project first for every dimension, matching getGroupByPropertiesForConfig in
 * invoicing_generate_lines_timeregs.js.
 *
 * The single-invoice flow can put several projects on one invoice, and without the project in the key
 * two projects that both have a phase called "Discovery" merge into one row — while the priced lines
 * beside them stay per-project. A row that silently spans projects also cannot be reconciled against
 * anything.
 */
const groupKeysFor = (grouping: string): string[] => {
	const dimension =
		grouping === TIME_REG_GROUPINGS.NO_GROUPING || grouping === TIME_REG_GROUPINGS.NO_GROUPING_NOTES ? 'id' : '__wdGroup';
	// Project first, so a group never straddles two projects.
	return ['__wdProject', dimension];
};

const inWindow = (tr: WorkDetailTimeReg, start?: string, end?: string): boolean => {
	const d = iso(tr);
	if (start && d < start) return false;
	if (end && d > end) return false;
	return true;
};

const round = (n: number, decimals: number): number => {
	const f = Math.pow(10, decimals);
	return Math.round(n * f) / f;
};

/** Grouping key for time whose project is absent, so it still groups rather than splitting per row. */
const NO_PROJECT = '__WORK_DETAIL_NO_PROJECT__';

/**
 * Qualifies a row's label with the project it belongs to, for an invoice whose rows span projects.
 *
 * Splitting by project is what stops two projects' "Discovery" merging into one row, but it leaves two
 * rows with the same label, which reads as a duplicate. Naming the project separates them.
 *
 * A null label is left alone: it means the grouping dimension was absent, and its wording is resolved
 * from the dimension when rendered rather than stored \u2014 so there is nothing here to qualify without
 * baking in a resolved, locale-specific string, which is exactly what must not be persisted.
 */
const qualifyWithProject = (label: string | null, projectName: string | null, grouping: string): string | null => {
	if (label === null || !projectName) return label;
	// The dimension already IS the project, so prefixing would print the name twice.
	if (grouping === TIME_REG_GROUPINGS.PROJECT) return label;
	return `${projectName} / ${label}`;
};

/**
 * The done-tasks grouping lists only time logged against done tasks, matching groupingFiltering in
 * invoicing_generate_lines_timeregs.js. Without this the dimension named "done tasks" also lists
 * tasks still in progress, so the two engines disagree about what the same dimension means.
 *
 * Time with no task is excluded too: it cannot be shown to be against a done task, and bucketing it
 * as ungrouped would assert work is finished that nothing says is.
 */
const inGroupingScope = (tr: WorkDetailTimeReg, grouping: string): boolean =>
	grouping !== TIME_REG_GROUPINGS.DONE_TASK || tr.task?.statusColumnV2?.category === WorkflowCategories.DONE;

export const buildWorkDetailRows = (timeRegs: WorkDetailTimeReg[], config: WorkDetailConfig): WorkDetailRow[] => {
	const eligible = (timeRegs ?? [])
		.filter(tr => inWindow(tr, config.startDate, config.endDate))
		.filter(tr => inGroupingScope(tr, config.grouping))
		.map(tr => ({
			...tr,
			__wdGroup: labelFor(tr, config.grouping),
			__wdProject: workDetailProject(tr)?.id ?? NO_PROJECT,
		}));

	// Only worth naming the project on a row when the rows actually span more than one — on a
	// single-project invoice it would repeat the same name on every line for nothing.
	const spansProjects = new Set(eligible.map(tr => tr.__wdProject)).size > 1;

	const grouped = groupByProperties(eligible, groupKeysFor(config.grouping));
	const rows: WorkDetailRow[] = [];

	visitGroups(grouped, (members: GroupedTimeReg[]) => {
		// minutesRegistered, not billableMinutesRegistered: work detail states the work that was
		// done, and on a fixed-price engagement non-billable time is still work that was done.
		const minutes = members.reduce((sum, m) => sum + (m.minutesRegistered ?? 0), 0);
		const dates = members.map(iso).sort();
		const hours = minutes / 60;

		let rate: number | null = null;
		let value: number | null = null;
		if (config.showRates) {
			// value is each registration's own rate applied to its own hours, converted into the
			// invoice's currency one registration at a time so a group spanning two rate-card
			// currencies still totals correctly. rate is then derived back out of value, so Hours,
			// Rate and Value always reconcile on the rendered document (rate x hours = value).
			//
			// Deliberately NOT the backend's `price`: that is the BILLABLE portion only, derived from
			// billableMinutesRegistered, while these rows state all the work that was done. Pairing
			// billable money with total hours understated the rate on every company using the billable
			// split, and made an all-non-billable group print 0.00 where it means "no charge".
			const toInvoiceCurrency = config.priceToInvoiceCurrency;
			const rated = members.filter(m => m.rate !== null && m.rate !== undefined);
			if (rated.length > 0) {
				value = round(
					rated.reduce((sum, m) => {
						const amount = (m.rate ?? 0) * ((m.minutesRegistered ?? 0) / 60);
						return sum + (toInvoiceCurrency ? toInvoiceCurrency(amount, m) : amount);
					}, 0),
					2
				);
				rate = hours > 0 ? round(value / hours, 2) : null;
			}
		}

		rows.push({
			key: members.map(m => m.id).join('|'),
			groupLabel: spansProjects
				? qualifyWithProject(
						members[0].__wdGroup === WORK_DETAIL_UNGROUPED_KEY ? null : members[0].__wdGroup,
						workDetailProject(members[0])?.name ?? null,
						config.grouping
				  )
				: members[0].__wdGroup === WORK_DETAIL_UNGROUPED_KEY
				? null
				: members[0].__wdGroup,
			sortOrder: rows.length,
			minutes,
			firstDate: dates[0] ?? null,
			lastDate: dates[dates.length - 1] ?? null,
			rate,
			value,
			removed: false,
		});
	});

	return rows.map((row, index) => ({...row, sortOrder: index}));
};

/**
 * Carries the operator's removals from a previous snapshot onto a freshly built one.
 *
 * Rebuilding is unavoidable: the panel is unmounted whenever the operator steps back through the
 * creation flow, and rebuilding on the way forward again is how a changed grouping or window takes
 * effect. Without this, that rebuild is also what silently reinstates every row the operator had
 * removed — they would have to notice and redo the removals, or send work detail they had chosen to
 * withhold.
 *
 * A removal is keyed on the row's `key`, which is the identity of the work behind it, so a removal
 * survives exactly as long as the row it referred to. Change the grouping and the keys change with
 * it, which is what makes those removals correctly disappear rather than reattach to unrelated rows.
 */
export const preserveWorkDetailRemovals = (rows: WorkDetailRow[], previousRows: WorkDetailRow[]): WorkDetailRow[] => {
	const removedKeys = new Set((previousRows ?? []).filter(row => row.removed).map(row => row.key));
	if (removedKeys.size === 0) return rows;
	return rows.map(row => (removedKeys.has(row.key) ? {...row, removed: true} : row));
};

/** Hours as displayed, honouring company.invoiceQuantityDecimals. */
export const workDetailHours = (minutes: number, quantityDecimals: number): number => round(minutes / 60, quantityDecimals);

/** Total minutes over the rows still shown. Removing a row changes this. */
export const workDetailTotalMinutes = (rows: WorkDetailRow[]): number =>
	rows.filter(r => !r.removed).reduce((sum, r) => sum + r.minutes, 0);

/** Total value over the rows still shown. Null when rates are not disclosed. */
export const workDetailTotalValue = (rows: WorkDetailRow[]): number | null => {
	const visible = rows.filter(r => !r.removed && r.value !== null);
	if (visible.length === 0) return null;
	return round(
		visible.reduce((sum, r) => sum + (r.value ?? 0), 0),
		2
	);
};
